// src/App.js
import React, { useState, useEffect } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';

// Importation des icônes Lucide React
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

// Importation du UserProvider et useUser du contexte
import { UserProvider, useUser } from './components/UserContext';

// Importation des composants
import Auth from './components/Auth';
import AdminDashboard from './components/AdminDashboard';
import AdminHome from './components/AdminHome';
import UserProfileForm from './components/UserProfileForm';

// Importez votre fichier d'initialisation Firebase.
import './firebase';


// --- Composant interne qui utilise le contexte pour décider quoi afficher ---
function AppContent({ messagesManager }) {
  // Récupère les informations de l'utilisateur du contexte
  const { userAuth, userProfile, isLoadingUser, errorLoadingUser, refreshUserProfile } = useUser();
  const { setMessage, setError, message, error } = messagesManager;

  // L'état 'showAdminDashboard'
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);

  // ÉTAT ET LOGIQUE HORS LIGNE (Déplacé dans AppContent)
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);


  // Fonction de déconnexion globale
  const handleLogout = async () => {
    try {
      await signOut(getAuth());
      setMessage({ type: 'success', text: 'Vous avez été déconnecté.' });
      setShowAdminDashboard(false);
    } catch (err) {
      console.error("Erreur de déconnexion:", err);
      setError("Échec de la déconnexion: " + err.message);
    }
  };

  // --- Rendu conditionnel basé sur l'état de l'utilisateur ---

  // 1. Affichage global de chargement
  if (isLoadingUser) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
        <p className="ml-3 text-lg text-gray-700">Chargement de l'application...</p>
      </div>
    );
  }

  // 2. Gestion des erreurs critiques de chargement du profil
  if (errorLoadingUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-50">
        <div className="p-8 bg-white rounded-lg shadow-lg text-center">
          <XCircle size={48} className="text-red-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Erreur Critique</h2>
          <p className="text-gray-600 mb-6">Impossible de charger les informations utilisateur : {errorLoadingUser.message}.</p>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white py-2.5 px-5 rounded-lg font-semibold text-base hover:bg-red-700"
          >
            Déconnexion
          </button>
        </div>
      </div>
    );
  }

  // 3. Utilisateur non authentifié : Afficher la page d'authentification
  if (!userAuth) {
    return (
      <Auth
        db={getFirestore()}
        auth={getAuth()}
        setMessage={setMessage}
        setError={setError}
        error={error}
        loading={isLoadingUser}
      />
    );
  }

  // 4. Utilisateur authentifié mais profil Firestore incomplet
  if (userAuth && (!userProfile || !userProfile.profileCompleted)) {
    return (
      <UserProfileForm
        db={getFirestore()}
        onProfileComplete={refreshUserProfile}
        setError={setError}
        setMessage={setMessage}
      />
    );
  }

  // 5. Utilisateur authentifié et profil Firestore complet : Afficher le tableau de bord ou la page d'accueil
  return (
    <>
      {/* Affichage du bandeau hors ligne au-dessus du contenu principal */}
      {isOffline && (
        <div className="fixed top-0 w-full bg-yellow-300 text-center p-2 z-50">
          Vous êtes hors ligne — certaines fonctionnalités peuvent être limitées
        </div>
      )}

      {showAdminDashboard ? (
        <AdminDashboard
          setMessage={setMessage}
          setError={setError}
          handleLogout={handleLogout}
          setShowAdminDashboard={setShowAdminDashboard}
          db={getFirestore()}
          storage={getStorage()}
        />
      ) : (
        <AdminHome
          setMessage={setMessage}
          setError={setError}
          handleLogout={handleLogout}
          setShowAdminDashboard={setShowAdminDashboard}
          db={getFirestore()}
          auth={getAuth()}
          storage={getStorage()}
        />
      )}
    </>
  );
}

// --- Composant principal de l'application ---
const App = () => {
  // États pour les messages d'erreur et de succès
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Gestion des messages de succès/erreur (nettoyage automatique)
  useEffect(() => {
    let timer;
    if (message) {
      // Pour les messages de type 'success', utilisez le champ 'text'
      const text = typeof message === 'object' && message !== null ? message.text : message;
      if (text) {
        timer = setTimeout(() => {
          setMessage(null);
        }, 5000);
      }
    }
    return () => clearTimeout(timer);
  }, [message]);

  useEffect(() => {
    let timer;
    if (error) {
      timer = setTimeout(() => {
        setError(null);
      }, 7000);
    }
    return () => clearTimeout(timer);
  }, [error]);


  const renderMessages = () => {
    return (
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {/* Correction pour s'assurer que message.text est accessible si 'message' est un objet */}
        {message && message.text && (
          <div
            className={`p-4 rounded-md shadow-lg flex items-center ${
              message.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle size={20} className="mr-2" />
            ) : (
              <XCircle size={20} className="mr-2" />
            )}
            <span>{message.text}</span>
          </div>
        )}
        {error && (
          <div className="p-4 rounded-md shadow-lg flex items-center bg-red-100 text-red-700">
            <XCircle size={20} className="mr-2" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  };

  const messagesManager = { setMessage, setError, message, error };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {renderMessages()}
      {/* UserProvider doit envelopper le composant AppContent */}
      <UserProvider>
        <AppContent messagesManager={messagesManager} />
      </UserProvider>
    </div>
  );
};

export default App;